import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { Form } from 'templates/Form';
import { Button, styled } from '@mui/material';
import { Button as StyledButton } from 'atoms/Button';
import { useUploadConsentForm } from 'hooks/u002/useUploadConsentFormHooks';
import { AppContext } from 'templates/AppContextProvider';
import { useParams, useSearchParams } from 'react-router-dom';
import LoadingModal from 'organisms/LoadingModal';
import { useValidateMailSession } from 'hooks/u002/useValidateMailSession';
import { PopupErrorDialog } from 'organisms/u002/PopupErrorDialog';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import MessageModal from 'organisms/MessageModal';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { CreateUploadConsentFormSchemaType, decryptExamNo } from 'commons/auditionForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUploadConsentFormSchema } from 'hooks/u002/useUploadConsentFormSchema';
import { SubmitHandler, useForm } from 'react-hook-form';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { useGetAudition } from 'hooks/u002/useGetAuditionHooks';
import { AuditionTopPage } from 'pages/AuditionFormTop';
import Cookies from 'js-cookie';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { NotFound } from 'pages/404';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/** 指定の桁数で文を丸め込む関数
 * @param str 文字列
 * @param options オプション
 *  - length: 文字数
 *  - ellips = ".": 省略記号
 *  - start = 4: 先頭から保持する桁数
 *  - end = 4: 末尾から保持する桁数
 * @returns 'abcd....xyz'
 */
function cropText(
  str: string,
  options: { length: number; ellips?: string; start?: number; end?: number }
): string {
  const { length, ellips = '.', start = 4, end = 4 } = options;
  if (str.length <= length) return str;
  const ellipsis = ellips.repeat(length - start - end);

  return str.substring(0, start) + ellipsis + str.substring(str.length - end);
}

export const ConsentFormUpload: FC = () => {
  const { currentLang, userId } = useContext(AppContext);
  const [specificUseConsent, setSpecificUseConsent] = useState<File | undefined>(undefined);
  const [privacyNoticeConsent, setPrivacyNoticeConsent] = useState<File | undefined>(undefined);
  const [termsConsent, setTermsConsent] = useState<File | undefined>(undefined);
  const [privacyPolicyConsent, setPrivacyPolicyConsent] = useState<File | undefined>(undefined);
  const [auditionTermsConsent, setAuditionTermsConsent] = useState<File | undefined>(undefined);
  const [evidenceConsent, setEvidenceConsent] = useState<File | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [popupErrorDialog, setPopupErrorDialog] = useState<string | undefined>(undefined);
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const routeParams = useParams();
  const { group, audition_id, examination_no: _examination_no } = routeParams;
  // パスパラメータのバリデーション
  if (!group || !audition_id || !_examination_no) {
    throw new Error('audition_id and examination_no is required');
  }
  const examination_no = useMemo(() => {
    let examination_no: number = NaN;
    try {
      const result = decryptExamNo(audition_id, _examination_no);
      examination_no = result.examNo;
      console.debug('decrypted examination_no:', examination_no);
      if (result.isSample) {
        console.warn('This is a sample examination_no');
      }
    } catch (e) {
      console.error(e);
      examination_no = NaN;
    }
    return examination_no;
  }, [audition_id, _examination_no]);
  const [searchParam] = useSearchParams();
  const sessionId = searchParam.get('sessionId');
  const representativeUser = searchParam.get('representative');
  const [validateMailSessionResult, validateMailSessionLoading, validateMailSessionErr] =
    useValidateMailSession({
      sessionId: sessionId ?? '',
      representative_user: representativeUser ?? '',
      lang: currentLang,
      audition_id: audition_id ?? '',
    });
  const [auditionData, isAuditionDataLoading, auditionDataErr] = useGetAudition({
    audition_id: audition_id ?? '',
    examination_no,
    lang: currentLang,
    representative_user: representativeUser ?? undefined,
  });
  const [uploadConsentForm, uploadLoading] = useUploadConsentForm();
  const CreateUploadConsentFormSchema = useUploadConsentFormSchema(
    representativeUser ? true : false,
    validateMailSessionResult?.specific_use_file_required ?? '0',
    validateMailSessionResult?.privacy_policy_file_required ?? '0',
    validateMailSessionResult?.audition_terms_file_required ?? '0'
  );
  useEffect(() => {
    // セッションIDのバリデーション
    if (!sessionId) {
      setPopupErrorDialog(tAuditionForm('TU002_079'));
      return;
    }
  }, [group, audition_id, _examination_no, sessionId, tAuditionForm]);

  useEffect(() => {
    if (validateMailSessionErr) {
      if (validateMailSessionErr.message === 'MU002F0125E') {
        setPopupErrorDialog(tAuditionForm('TU002_079'));
      } else if (validateMailSessionErr.message === 'MU002F0172E') {
        setPopupErrorDialog(tAuditionForm('TU002_135'));
      } else {
        setPopupErrorDialog(tAuditionForm('TU002_050'));
      }
    }
  }, [validateMailSessionErr, tAuditionForm]);
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<CreateUploadConsentFormSchemaType>({
    resolver: zodResolver(CreateUploadConsentFormSchema),
    shouldFocusError: true,
  });

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  if (isNaN(examination_no)) {
    return <NotFound />;
  }
  if (auditionDataErr) {
    let errorMessage = tAuditionForm('TU002_050');
    if (auditionDataErr.message === 'MU002F0115E') {
      errorMessage = tAuditionForm('TU002_062');
    }
    if (auditionDataErr.message === 'MU002F0125E') {
      errorMessage = tAuditionForm('TU002_079');
    }
    return (
      <AuditionTopPage title={tAuditionForm('TU002_050')}>
        <p className="text-[--c-error]">{errorMessage}</p>
      </AuditionTopPage>
    );
  }
  if (auditionData == null) {
    return <></>;
  }

  const now = auditionData.now_dtm;
  // 募集前の場合
  if (now < auditionData.start_dtm) {
    return <AuditionTopPage title={auditionData.title}>{auditionData.start_txt}</AuditionTopPage>;
  }

  // 募集終了後の場合
  if (auditionData.end_dtm < now) {
    console.log('募集終了後の場合', auditionData.end_dtm);
    return <AuditionTopPage title={auditionData.title}>{auditionData.end_txt}</AuditionTopPage>;
  }

  // 現在審査Noが違う場合
  if (examination_no != null && examination_no !== Number(examination_no)) {
    console.log('現在審査Noが違う場合', auditionData.examination_no);
    return <AuditionTopPage title={auditionData.title}>{auditionData.end_txt}</AuditionTopPage>;
  }

  const onSubmit: SubmitHandler<CreateUploadConsentFormSchemaType> = async (form) => {
    await uploadConsentForm({
      request: {
        sessionId: sessionId ?? '',
        lang: currentLang,
        audition_id: audition_id ?? '',
        representative_user: representativeUser ?? '',
        terms_file: form.term_of_service_file,
        privacy_notice_file: form.privacy_notice_file,
        specific_use_file: form.specific_use_file ?? undefined,
        privacy_policy_file: form.privacy_policy_file ?? undefined,
        audition_terms_file: form.audition_terms_file ?? undefined,
        evidence_file: form.evidence_file,
        user_id: userId ?? '',
      },
      onSuccess: () => {
        Cookies.remove('userBirthday');
        setIsSubmitted(true);
      },
      onError: (error) => {
        console.error({ error });
        setErrorMessage(tAuditionForm('TU002_111'));
      },
    });
  };

  // 同意書送信完了画面
  if (isSubmitted) {
    return (
      <>
        <AppLogoHeader />
        <PageContent>
          <Form title={tAuditionForm('TU002_080')} id="submission-complete-form">
            <div
              className={'min-h-[20vh] flex flex-col text-center items-center justify-center'}
              aria-live="polite"
              role="region"
              aria-labelledby="submission-complete-form"
            >
              <p>{tAuditionForm('TU002_081')}</p>
              <p>{tAuditionForm('TU002_082')}</p>
              <p>{tAuditionForm('TU002_083')}</p>
            </div>
          </Form>
        </PageContent>
        <UnauthAppFooter />
      </>
    );
  }

  // 同意書送信フォーム
  return (
    <>
      {(validateMailSessionLoading || uploadLoading || isAuditionDataLoading) && <LoadingModal />}
      {popupErrorDialog && <PopupErrorDialog message={popupErrorDialog} />}
      {errorMessage && (
        <MessageModal
          message={errorMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={() => {
            setErrorMessage(undefined);
          }}
        />
      )}
      <AppLogoHeader />
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_080')}>
            <div className={'flex justify-center items-center flex-col'}>
              <div className="flex flex-col w-full my-4">
                <FormSectionTitle text={tAuditionForm('TU002_103')} htmlFor={'termsConsent'} />
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  color="primary"
                  tabIndex={-1}
                  sx={{
                    mt: 2,
                    width: '14rem',
                    fontWeight: 'bold',
                    borderWidth: 3,
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: 4,
                      borderColor: 'primary.main',
                    },
                  }}
                  id={'termsConsent'}
                  title={tAuditionForm('TU002_084')}
                >
                  {termsConsent ? (
                    cropText(termsConsent.name, { length: 20 })
                  ) : (
                    <>
                      {tAuditionForm('TU002_084')}
                      <FileUploadIcon sx={{ ml: 1 }} />
                    </>
                  )}

                  <VisuallyHiddenInput
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.length !== 1) return;
                      setTermsConsent(e.target.files[0]);
                      setValue('term_of_service_file', e.target.files[0]);
                    }}
                  />
                </Button>
                <FieldErrorMessages messages={[errors.term_of_service_file?.message]} />
              </div>
              <div className="flex flex-col w-full my-4">
                <FormSectionTitle text={tAuditionForm('TU002_104')} htmlFor={'privacyConsent'} />
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  color="primary"
                  tabIndex={-1}
                  sx={{
                    mt: 2,
                    width: '14rem',
                    fontWeight: 'bold',
                    borderWidth: 3,
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: 4,
                      borderColor: 'primary.main',
                    },
                  }}
                  id={'privacyConsent'}
                  title={tAuditionForm('TU002_084')}
                >
                  {privacyNoticeConsent ? (
                    cropText(privacyNoticeConsent.name, { length: 20 })
                  ) : (
                    <>
                      {tAuditionForm('TU002_084')}
                      <FileUploadIcon sx={{ ml: 1 }} />
                    </>
                  )}
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.length !== 1) return;
                      setPrivacyNoticeConsent(e.target.files[0]);
                      setValue('privacy_notice_file', e.target.files[0]);
                    }}
                  />
                </Button>
                <FieldErrorMessages messages={[errors.privacy_notice_file?.message]} />
              </div>
              {validateMailSessionResult?.specific_use_file_required === '1' && (
                <div className="flex flex-col w-full my-4">
                  <FormSectionTitle text={tAuditionForm('TU002_105')} htmlFor={'specificConsent'} />
                  <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    color="primary"
                    tabIndex={-1}
                    sx={{
                      mt: 2,
                      width: '14rem',
                      fontWeight: 'bold',
                      borderWidth: 3,
                      borderColor: 'primary.main',
                      '&:hover': {
                        borderWidth: 4,
                        borderColor: 'primary.main',
                      },
                    }}
                    id={'specificConsent  '}
                    title={tAuditionForm('TU002_084')}
                  >
                    {specificUseConsent ? (
                      cropText(specificUseConsent.name, { length: 20 })
                    ) : (
                      <>
                        {tAuditionForm('TU002_084')}
                        <FileUploadIcon sx={{ ml: 1 }} />
                      </>
                    )}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.length !== 1) return;
                        setSpecificUseConsent(e.target.files[0]);
                        setValue('specific_use_file', e.target.files[0]);
                      }}
                    />
                  </Button>
                  <FieldErrorMessages messages={[errors.specific_use_file?.message]} />
                </div>
              )}
              {validateMailSessionResult?.privacy_policy_file_required === '1' && (
                <div className="flex flex-col w-full my-4">
                  <FormSectionTitle
                    text={tAuditionForm('TU002_119')}
                    htmlFor={'privacyPolicyConsent'}
                  />
                  <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    color="primary"
                    tabIndex={-1}
                    sx={{
                      mt: 2,
                      width: '14rem',
                      fontWeight: 'bold',
                      borderWidth: 3,
                      borderColor: 'primary.main',
                      '&:hover': {
                        borderWidth: 4,
                        borderColor: 'primary.main',
                      },
                    }}
                    id={'privacyPolicyConsent'}
                    title={tAuditionForm('TU002_084')}
                  >
                    {privacyPolicyConsent ? (
                      cropText(privacyPolicyConsent.name, { length: 20 })
                    ) : (
                      <>
                        {tAuditionForm('TU002_084')}
                        <FileUploadIcon sx={{ ml: 1 }} />
                      </>
                    )}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.length !== 1) return;
                        setPrivacyPolicyConsent(e.target.files[0]);
                        setValue('privacy_policy_file', e.target.files[0]);
                      }}
                    />
                  </Button>
                  <FieldErrorMessages messages={[errors.privacy_policy_file?.message]} />
                </div>
              )}
              {validateMailSessionResult?.audition_terms_file_required === '1' && (
                <div className="flex flex-col w-full mx-auto my-4">
                  <FormSectionTitle
                    text={tAuditionForm('TU002_120')}
                    htmlFor={'auditionTermsConsent'}
                  />
                  <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    color="primary"
                    tabIndex={-1}
                    sx={{
                      mt: 2,
                      width: '14rem',
                      fontWeight: 'bold',
                      borderWidth: 3,
                      borderColor: 'primary.main',
                      '&:hover': {
                        borderWidth: 4,
                        borderColor: 'primary.main',
                      },
                    }}
                    id={'auditionTermsConsent'}
                    title={tAuditionForm('TU002_084')}
                  >
                    {auditionTermsConsent ? (
                      cropText(auditionTermsConsent.name, { length: 20 })
                    ) : (
                      <>
                        {tAuditionForm('TU002_084')}
                        <FileUploadIcon sx={{ ml: 1 }} />
                      </>
                    )}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.length !== 1) return;
                        setAuditionTermsConsent(e.target.files[0]);
                        setValue('audition_terms_file', e.target.files[0]);
                      }}
                    />
                  </Button>
                  <FieldErrorMessages messages={[errors.audition_terms_file?.message]} />
                </div>
              )}
              <div className="flex flex-col w-full mx-auto my-4">
                <FormSectionTitle text={tAuditionForm('TU002_106')} htmlFor={'consentEvidence'} />
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  color="primary"
                  tabIndex={-1}
                  sx={{
                    mt: 2,
                    width: '14rem',
                    fontWeight: 'bold',
                    borderWidth: 3,
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderWidth: 4,
                      borderColor: 'primary.main',
                    },
                  }}
                  id={'consentEvidence'}
                  title={tAuditionForm('TU002_084')}
                >
                  {evidenceConsent ? (
                    cropText(evidenceConsent.name, { length: 20 })
                  ) : (
                    <>
                      {tAuditionForm('TU002_084')}
                      <FileUploadIcon sx={{ ml: 1 }} />
                    </>
                  )}
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.length !== 1) return;
                      setEvidenceConsent(e.target.files[0]);
                      setValue('evidence_file', e.target.files[0]);
                    }}
                  />
                </Button>
                <FieldErrorMessages messages={[errors.evidence_file?.message]} />
              </div>

              <StyledButton
                className="w-[10rem] mt-20"
                label={tAuditionForm('TU002_078')}
                type="submit"
              />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
